/**
 * Every tunable the voyage layer has, in one file, each labelled MEASURED or MODELLED
 * with its unit and where the number came from.
 *
 * MEASURED means a command was run to get it and the command is named.
 * MODELLED means it was chosen or inferred. A modelled number reported as measured is
 * the worst thing that can happen in this run, so the label is part of the name's
 * documentation and never dropped when a number is quoted elsewhere.
 *
 * -- THE SINGLE EDIT, WHICH ROUND 2 ADVERTISED AND DID NOT HAVE ---------------
 *
 * Round 2's version of this file said: "Every other geometry constant here is a function
 * of this one, so when world scale lands, this is the single edit." That was MEASURED
 * false. Five league-valued constants were not functions of it - the spiral pitch, both
 * field-noise lengths, the cruise speed and the on-track epsilon - so making the
 * advertised single edit at 100x broke this file's OWN documented invariant, that
 * successive coils stay further apart than two sighting radii.
 *
 * The shape is now separated from the scale, and neither is a literal in the middle of a
 * derivation:
 *
 *   WorldScale  (scale.ts)   metres, pixels and the size of the sea. AN INPUT. One named
 *                            source, mirrored, with an instrument that fails when the
 *                            mirror and the source disagree.
 *   VoyageShape (this file)  the DIMENSIONLESS ratios this lane owns - how many leagues a
 *                            screen is worth, how tight the spiral is IN SCREEN WIDTHS,
 *                            how coarse the noise is in screen widths, and so on.
 *   voyageGeometry(scale, shape)
 *                            every league-valued constant, as a pure function of the two.
 *
 * That makes the single-edit claim TESTABLE rather than advertised, and `scale.test.ts`
 * tests it: it calls `voyageGeometry` with a shape whose leagues-per-screen is 100x and
 * asserts EVERY league-valued output moved by exactly 100x and EVERY dimensionless output
 * did not move at all. A constant that is not a function of the input fails that check,
 * which is the failure round 2 shipped.
 */

import type { WorldScale } from './scale';
import {
  PUBLISHED_WORLD_SCALE,
  frameWidthMetres,
  pixelAspect,
  screensOfSea,
  seaDiameterFrameWidths,
  worldAspect,
} from './scale';

// ---------------------------------------------------------------------------
// The shape. Dimensionless, owned by this lane, and free of world scale.
// ---------------------------------------------------------------------------

/**
 * Everything about the sea's geometry that survives a change of world scale. Every field
 * is dimensionless or a time, and none of them may be a length.
 */
export interface VoyageShape {
  /**
   * MODELLED. THE MAP GRAIN: how many leagues one screen width is worth. This is the
   * definition of a Reelin league, and the reason `metresPerLeague` is an output rather
   * than an input. 24 gives a league of about 5.8 m at the published scale - a map unit,
   * not a nautical league; see scale.ts.
   */
  readonly leaguesPerFrameWidth: number;
  /**
   * MODELLED, radians per screen width of outward progress. The spiral's tightness.
   * 7.5 puts successive coils 2*PI/7.5 = 0.838 screen widths apart, so the previous
   * coil's islands are still near enough to read as the same sea rather than a separate
   * map. HARD CONSTRAINT, asserted in track.test.ts: that spacing must exceed TWO SIGHTING
   * RADII, or a place on coil n is sighted while the boat is still on coil n-1 and
   * discovery order stops matching progression order.
   *
   * The bound is NOT written here as a number. Round 3 documented it as "0.5625 screen
   * widths", which was 2 x 6.75 / 24 - the sighting radius from BEFORE the world-aspect
   * correction forty lines below, so a third of the stated headroom had silently gone
   * (CL3-C-06). track.test.ts computes it from SIGHTING_RADIUS_LEAGUES and this docstring
   * quotes no figure, because a margin written in prose is a margin that can go stale.
   */
  readonly spiralRadiansPerFrameWidth: number;
  /** MODELLED, screen widths. Lattice period of the field noise. */
  readonly noisePeriodFrameWidths: number;
  /** MODELLED, screen widths. Peak lateral wander the noise adds to a field isoline. */
  readonly noiseAmplitudeFrameWidths: number;
  /**
   * MODELLED, dimensionless. HOW MUCH FASTER THAN THE SOURCE'S COZY SAILING PACE A BOAT
   * UNDER ORDERS GOES. The passage speed is this times `cozyPaceMetresPerSecond`, which is
   * the one pace figure on this run that was measured by somebody else.
   *
   * It replaces round 3's `cruiseSecondsPerFrameWidth`, and the replacement is the point.
   * That constant was free of the cozy pace, so the "within 0.5x..2x of cozy" check in
   * scale.test.ts was a real bound on a free number - but it was applied to the 2.63% of
   * journeys the clamp left alone, while the 97.37% running at 12.31x cozy was bounded by
   * nothing at all (CL3-C-02). Deriving the speed FROM the cozy pace makes that particular
   * check a tautology, so it is deleted; the bound that replaced it compares the passage
   * speed against the boat's own natural pace, which comes from the progression curve and
   * shares no machinery with this number. See journey.ts.
   */
  readonly passageCozyMultiple: number;
  /** MODELLED, screen widths. Below this the boat counts as on its track. */
  readonly onTrackEpsilonFrameWidths: number;
  /**
   * MODELLED, dimensionless. Of the hours a settled round spends under way, the fraction
   * spent lying to at EACH end of it - at the farthest ground, and at home.
   *
   * It is what turns the round from a shuttle into a boat that calls somewhere: the
   * position stands still, the 180-degree come-about is spread across these hours instead
   * of happening between two frames, and the readout can name the place the boat is lying
   * at. 0.025 of a MEASURED 48.2-hour passage is about 1.2 hours at each end.
   */
  readonly settledRestFraction: number;
}

/**
 * The shape in force. Every value reproduces round 2's league constant EXACTLY at the
 * published map grain, bit for bit, which is asserted in scale.test.ts so this refactor
 * can be told apart from a retune.
 */
export const VOYAGE_SHAPE: VoyageShape = {
  leaguesPerFrameWidth: 24,
  spiralRadiansPerFrameWidth: 7.5,
  noisePeriodFrameWidths: 8 / 3,
  noiseAmplitudeFrameWidths: 0.75,
  passageCozyMultiple: 2.5,
  onTrackEpsilonFrameWidths: 1 / 24_000,
  settledRestFraction: 0.025,
};

// ---------------------------------------------------------------------------
// The geometry, as a pure function of (scale, shape).
// ---------------------------------------------------------------------------

/** Every length this lane uses, plus the derived figures the other lanes read. */
export interface VoyageGeometry {
  /** The inputs, carried so a failure message can name what produced the numbers. */
  readonly scale: WorldScale;
  readonly shape: VoyageShape;

  // Dimensionless. Must not move when the map grain moves.
  /** How much WORLD the frame holds across over how much it holds up. Geometry uses this. */
  readonly worldAspect: number;
  /** The frame's shape in pixels. Never used to convert a world length; see scale.ts. */
  readonly pixelAspect: number;
  readonly seaDiameterFrameWidths: number;
  readonly screensOfSea: number;

  // Lengths in leagues. Every one must move exactly with the map grain.
  readonly frameWidthLeagues: number;
  readonly frameHeightLeagues: number;
  readonly sightingRadiusLeagues: number;
  readonly spiralLeaguesPerRadian: number;
  readonly worldRadiusLeagues: number;
  readonly opennessScaleLeagues: number;
  readonly chillScaleLeagues: number;
  readonly fieldNoisePeriodLeagues: number;
  readonly fieldNoiseAmplitudeLeagues: number;
  readonly onTrackEpsilonLeagues: number;
  /** Leagues per second: a length over a time, so it moves with the map grain too. */
  readonly sailPassageLeaguesPerSecond: number;

  /** DERIVED from the scale. Metres one league is worth. An output, never an input. */
  readonly metresPerLeague: number;
}

/**
 * THE ONE PLACE A LEAGUE IS DEFINED. Pure, total, and with no reference to any module
 * constant, so the same function can be called with a hypothetical scale in a test.
 */
export function voyageGeometry(scale: WorldScale, shape: VoyageShape): VoyageGeometry {
  const frameWidthLeagues = shape.leaguesPerFrameWidth;
  // THE WORLD ASPECT, not the pixel aspect. A league is a world length, so converting a
  // frame width into a frame height with 16:9 would be converting metres with pixels -
  // which is exactly what round 2 did, and is why its sighting radius was 6.75 instead of
  // 7.80. The camera is tilted; the frame holds a different amount of world in each axis.
  const aspect = worldAspect(scale);
  const frameHeightLeagues = frameWidthLeagues / aspect;
  const sightingRadiusLeagues = frameHeightLeagues / 2;
  const seaFrames = seaDiameterFrameWidths(scale);
  const worldRadiusLeagues = (frameWidthLeagues * seaFrames) / 2;
  const metresPerLeague = frameWidthMetres(scale) / frameWidthLeagues;

  return {
    scale,
    shape,

    worldAspect: aspect,
    pixelAspect: pixelAspect(scale),
    seaDiameterFrameWidths: seaFrames,
    screensOfSea: screensOfSea(scale),

    frameWidthLeagues,
    frameHeightLeagues,
    sightingRadiusLeagues,
    spiralLeaguesPerRadian: frameWidthLeagues / shape.spiralRadiansPerFrameWidth,
    worldRadiusLeagues,
    opennessScaleLeagues: worldRadiusLeagues,
    chillScaleLeagues: 2 * worldRadiusLeagues,
    fieldNoisePeriodLeagues: frameWidthLeagues * shape.noisePeriodFrameWidths,
    fieldNoiseAmplitudeLeagues: frameWidthLeagues * shape.noiseAmplitudeFrameWidths,
    onTrackEpsilonLeagues: frameWidthLeagues * shape.onTrackEpsilonFrameWidths,
    // A SPEED IN LEAGUES, DERIVED FROM A SPEED IN METRES. It is still a length over a
    // time, so it moves exactly with the map grain: metresPerLeague is inversely
    // proportional to leaguesPerFrameWidth, so this is directly proportional to it, and
    // scale.test.ts's 100x single-edit check covers it like every other league quantity.
    sailPassageLeaguesPerSecond: (shape.passageCozyMultiple * scale.cozyPaceMetresPerSecond) / metresPerLeague,

    metresPerLeague,
  };
}

/** The geometry in force: the published scale, applied to this lane's shape. */
export const VOYAGE_GEOMETRY: VoyageGeometry = voyageGeometry(PUBLISHED_WORLD_SCALE, VOYAGE_SHAPE);

// ---------------------------------------------------------------------------
// Framing. Every one of these is now an OUTPUT of the geometry above.
// ---------------------------------------------------------------------------

/**
 * DERIVED, dimensionless: 640/360 = 1.7778 (16:9). THE FRAME'S SHAPE IN PIXELS.
 *
 * MEASURED cross-check, and the reason this stopped being a literal: read out of the SOF
 * marker of all 77 reference JPEGs in `gauntlet/refs/images` (1920x1080 x69, 1280x720 x2,
 * 1600x900 x1, 1276x716 x5) - 72 of 77 exactly 16:9, the 5 outliers within 0.25%. That
 * measurement is now an assertion in scale.test.ts rather than a second copy of 16/9.
 *
 * IT MAY NOT BE USED TO CONVERT A WORLD LENGTH. Round 2 divided a frame width in LEAGUES
 * by this to get a frame height in leagues, which converts metres by a ratio of pixels.
 * Use FRAME_WORLD_ASPECT for anything that touches the sea.
 */
export const FRAME_PIXEL_ASPECT = VOYAGE_GEOMETRY.pixelAspect;

/**
 * DERIVED, dimensionless: 123.08 m / 80.00 m = 1.5385. HOW MUCH WORLD THE FRAME HOLDS.
 *
 * The camera is a three-quarter view at theta = 60 degrees, so the frame is foreshortened
 * up the screen and holds relatively MORE world in that axis per pixel. art-technique's
 * §4.6 handback: "the anisotropy is new and it is not cosmetic". Everything in this file
 * that converts across into up uses this and never the pixel aspect.
 */
export const FRAME_WORLD_ASPECT = VOYAGE_GEOMETRY.worldAspect;

/**
 * DERIVED, leagues. How much world the camera holds across at the wide framing the owner
 * ruled (several islands in view, boat small, sea ~70% of frame).
 *
 * It is `VOYAGE_SHAPE.leaguesPerFrameWidth` by definition - a league IS a twenty-fourth
 * of a screen width. The number world scale actually moves is `METRES_PER_LEAGUE`, and
 * the ratio it refuted is `SEA_DIAMETER_FRAME_WIDTHS`.
 */
export const FRAME_WIDTH_LEAGUES = VOYAGE_GEOMETRY.frameWidthLeagues;

/** DERIVED from FRAME_WIDTH_LEAGUES and the frame's WORLD aspect. Leagues. */
export const FRAME_HEIGHT_LEAGUES = VOYAGE_GEOMETRY.frameHeightLeagues;

/**
 * DERIVED, leagues. A place is discovered when the boat's natural drift brings it
 * within half the SHORTER frame dimension IN WORLD UNITS, i.e. when it was unambiguously
 * on screen. Half-height rather than half-width so the guarantee holds in both axes.
 *
 * 7.80 leagues at the corrected scale, against round 2's 6.75. The whole of that change is
 * the pixel-aspect-for-world-aspect substitution described on FRAME_WORLD_ASPECT; the map
 * grain did not move.
 */
export const SIGHTING_RADIUS_LEAGUES = VOYAGE_GEOMETRY.sightingRadiusLeagues;

/** DERIVED, metres per league. An OUTPUT of the published scale; see scale.ts. */
export const METRES_PER_LEAGUE = VOYAGE_GEOMETRY.metresPerLeague;

// ---------------------------------------------------------------------------
// The track. An Archimedean spiral out of the home water, r = A * theta.
// ---------------------------------------------------------------------------

/** DERIVED from VOYAGE_SHAPE.spiralRadiansPerFrameWidth. Leagues per radian. */
export const SPIRAL_LEAGUES_PER_RADIAN = VOYAGE_GEOMETRY.spiralLeaguesPerRadian;

/**
 * DERIVED, leagues. Outer radius of the charted sea.
 *
 * ROUND 2 HARD-CODED THIS AS `8 * FRAME_WIDTH_LEAGUES`, i.e. a sea 16 screens across,
 * and carried no marker on the 8 while the constant beside it carried a BLOCKED UPSTREAM
 * notice. The published scale requires 49.59 screens across, so that literal was a
 * refutation by a factor of 3.1 in linear size and 9.6 in area, and it was the only place
 * the disagreement lived. It is now an output of the scale, and scale.test.ts fails if
 * the two ever part company again.
 */
export const WORLD_RADIUS_LEAGUES = VOYAGE_GEOMETRY.worldRadiusLeagues;

/** DERIVED, dimensionless. Screen widths across the whole sea. Round 2's silent 16. */
export const SEA_DIAMETER_FRAME_WIDTHS = VOYAGE_GEOMETRY.seaDiameterFrameWidths;

// ---------------------------------------------------------------------------
// Pace. The drift rate is a FUNCTION of these, never a literal (see pacing.ts).
// ---------------------------------------------------------------------------

/**
 * THERE IS NO PROGRESSION RATE IN THIS FILE, AND THAT IS THE POINT.
 *
 * Round 1 carried `REFERENCE_PROGRESS_PER_HOUR = 100` here, MODELLED and honestly
 * labelled, and solved the whole pace against it while the only progression source in
 * the repository paid 6,604 XP in its first hour. Nothing failed when the two disagreed,
 * because the simulation had never heard of the constant. Round 1's critic measured the
 * consequence through the obvious wiring: first landfall at 40 seconds instead of half
 * an hour, and the entire twenty-place sea seen in 51 minutes.
 *
 * The constant is deleted rather than corrected. A rate that lives on one side of a seam
 * is not a shared number, and re-tuning it would have left the same shape in place. The
 * pace is now solved against a ProgressionCurve the progression owner supplies, and the
 * curve's identity is checked at the point of use - see pacing.ts and sim-bridge.ts.
 */

/**
 * MODELLED, hours. Drift before the first place other than home comes into sight, and
 * THE ONE KNOB THE CHARTED SPAN IS BOUGHT WITH.
 *
 * Round 2's CL2-R2-01 said the span "is bought with WORLD SIZE and CONTENT, and never
 * with the shape of the curve or the pace constant". That is MEASURED false and the
 * measurement is `tools/lanes/core-loop/span.test.ts`: with places, layout and world
 * untouched, this constant alone moves the span across 13.5 h to 290.7 h as it goes from
 * 0.25 h to 5.3 h, very nearly linearly. World size does not move it at all, because the
 * layout is laid out in FRACTIONS of the charted track, which makes the span exactly
 * scale-invariant.
 *
 * So this is a PACING CONSTANT, it is the whole of the span, and it is fenced in exactly
 * one place: CHARTED_SPAN_HOURS_BAND, below.
 */
export const FIRST_LANDFALL_HOURS = 0.5;

/**
 * MODELLED, hours. THE ONE LIMIT ON THE CHARTED SPAN. One place, one number.
 *
 * Round 2 fenced this quantity in THREE places with TWO different ceilings -
 * `SPAN_BAND_HOURS = {min: 8, max: 200}` and a bare `toBeLessThan(200)` in
 * pace-agreement.test.ts, and `> 40 && < 70` on the placeholder in pacing.test.ts - so
 * the effective rule was the tightest one and no reader could tell which was the design
 * intent. `span.test.ts` now asserts that no test file in this lane compares a charted
 * span against any numeric literal except through this constant, so a fourth fence
 * cannot be added quietly.
 *
 * THE FLOOR is the anti-bar at the short end: a whole sea seen out in one evening. THE
 * CEILING is the named anti-bar at the long end - "a waiting-simulator endgame",
 * CONTRACT.md section 1.4 - expressed in WALL-CLOCK HOURS, which is the unit the
 * anti-bar is stated in and the only unit the player experiences. Hundreds of hours of
 * game is a job for world size and content; hundreds of hours to see the FIRST sea is
 * the anti-bar itself.
 */
export const CHARTED_SPAN_HOURS_BAND = { min: 8, max: 200 } as const;

/**
 * MODELLED, dimensionless: offline holds. THE ONE LIMIT ON THE LANDFALL CADENCE, in the
 * unit the anti-bar is stated in.
 *
 * CL2-R2-E's second half: this lane instrumented cadence only as a FRACTION of the
 * charted span (the measured Melvor 10/95 gap), never in wall-clock hours - "which is the
 * unit the anti-bar is stated in and the only unit the player experiences". A fraction
 * cannot see a waiting simulator, because every span has the same fractions.
 *
 * The bound is expressed in HOLDS rather than hours so that it has no number of its own
 * to disagree with sim-core's: the hours come from OFFLINE_HOLD_HOURS, through
 * `holdDivergenceHours` in sim-bridge.ts. ONE HOLD is the honest ceiling, and the
 * reasoning is the player's: the hold is exactly how much of an absence the game pays
 * for, so a player who leaves for the longest absence that pays in full and comes back to
 * find NOTHING NEW IN SIGHT has been made to wait rather than to idle. That is the
 * waiting-simulator anti-bar, and it is now a number.
 *
 * MEASURED headroom, tools/lanes/core-loop/results/span-sweep.json: the largest gap in
 * force is 3.084 h against a 24 h ceiling. The fence bites at FIRST_LANDFALL_HOURS near
 * 3.9 - which is the point, because round 2's only real fence on that constant was a
 * placeholder-curve assertion capped at 70 hours of span.
 */
export const LANDFALL_GAP_HOLDS_MAX = 1;

/**
 * MEASURED, levels. The 19 Melvor Idle Fishing levels in 1-99 that unlock a fish,
 * parsed from the rendered wiki tables and recorded in gauntlet/refs/bar-depth.json.
 * This list is the ONLY place the cadence numbers are written down; everything below
 * is derived from it, so there is no second copy to disagree with it.
 */
export const MELVOR_UNLOCK_LEVELS: readonly number[] = [
  1, 5, 8, 10, 15, 20, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 95,
];

/** MEASURED, levels. The level range the cadence was measured over. Same source. */
export const CADENCE_LEVEL_SPAN = 99;

function maxGap(levels: readonly number[]): number {
  let largest = 0;
  for (let i = 1; i < levels.length; i += 1) {
    const a = levels[i - 1];
    const b = levels[i];
    if (a === undefined || b === undefined) continue;
    largest = Math.max(largest, b - a);
  }
  return largest;
}

/** DERIVED from the list: 10 levels (20->30 and 85->95). */
export const CADENCE_MAX_GAP_LEVELS = maxGap(MELVOR_UNLOCK_LEVELS);

/** DERIVED from the list: 95. */
export const CADENCE_LAST_UNLOCK_LEVEL = MELVOR_UNLOCK_LEVELS[MELVOR_UNLOCK_LEVELS.length - 1] ?? CADENCE_LEVEL_SPAN;

/**
 * DERIVED: 10/95 = 0.10526.
 *
 * The denominator is the LAST UNLOCK, not the level span, because the charted span this
 * bound is applied to also runs from home to the last charted place. Measured against
 * the full 1-99 range the same gap is 10/99 = 0.10101; the two differ only in which
 * end-point they call "the end", and mixing them would compare our chart against a
 * longer Melvor than the one that was measured.
 *
 * The rule it buys: never more than a tenth of the charted sea with nothing new coming
 * into sight.
 */
export const CADENCE_MAX_GAP_FRACTION = CADENCE_MAX_GAP_LEVELS / CADENCE_LAST_UNLOCK_LEVEL;

/**
 * NUMERICAL, dimensionless. The only slack the cadence check is allowed.
 *
 * Round 1's check compared the measured gap against a bound the same module widened by
 * two sighting radii - a slack of 0.00244 covering a margin of 0.00231, so the layout
 * exceeded the measured Melvor bar and the test passed anyway. The widening is gone. The
 * layout is now SOLVED so that each place's sighting distance lands on its cadence
 * fraction, and the only tolerance left is the residual of that fixed-count solve.
 *
 * It is a property of the solver, not of the world: it does not contain a sighting
 * radius, a span or any other geometry, so it cannot grow when the world does.
 * chart.test.ts asserts it stays under a thousandth of the bar it decorates.
 */
export const CADENCE_SOLVE_TOLERANCE = 1e-6;

// ---------------------------------------------------------------------------
// Journeys. Auto-sail only; there is no steering input anywhere in this module.
// ---------------------------------------------------------------------------

/**
 * DERIVED, leagues per second. THE PASSAGE SPEED: how fast the boat goes under orders.
 *
 * It is the boat's speed for every journey longer than JOURNEY_MIN_SECONDS worth of water,
 * with no clamp above it and therefore no range over which it is a lie. Round 1 and round
 * 3 both published a "cruise" figure that described a minority of journeys - MEASURED at
 * 2.63% of the 380 ordered pairs on the shipped chart, while the other 97.37% ran at
 * 12.31x the cozy pace because a ceiling clamp was reconciling a three-minute promise with
 * a 6.9 km sea (CL3-C-02). The clamp is gone; see journey.ts.
 *
 * MEASURED consequence at the published scale: 2.5 x 2.572 = 6.43 m/s = 12.5 knots, which
 * is 1.54x the fastest the boat's own natural drift ever gets (4.177 m/s, at the moment
 * the charted sea ends and for ever after - see settled.ts). That margin is the bound in
 * journey.test.ts, and it is a comparison between two numbers that share no derivation:
 * this one comes from the world scale document, the other from the simulation's XP curve.
 */
export const SAIL_PASSAGE_LEAGUES_PER_SECOND = VOYAGE_GEOMETRY.sailPassageLeaguesPerSecond;

/** MODELLED, seconds. A journey is a journey; nothing is ever a teleport. */
export const JOURNEY_MIN_SECONDS = 8;

/**
 * MODELLED, minutes. THE WAYFINDING BOUND, and it is no longer enforced by a clamp.
 *
 * Round 3 stated this promise as JOURNEY_CEILING_SECONDS = 180 and enforced it with
 * `clamp(d / cruise, min, ceiling)`, so the test that checked it - `duration <= ceiling` -
 * was the clamp's own postcondition and passed at a world size of 9.007e15 leagues and an
 * implied 2.566e14 m/s. Nothing could make it fail (CL3-C-02).
 *
 * What is enforced now is a MEASUREMENT over the chart the game actually ships: the
 * longest passage between any two charted places, at the passage speed, must come in under
 * this. It fails if the sea grows, if the layout spreads, or if the speed drops, and
 * journey.test.ts carries the negative control that proves it - the same predicate over a
 * sea twice this size goes red.
 *
 * TWENTY MINUTES, against a MEASURED longest passage of 14.78 minutes. The promise it
 * replaces was three minutes, and this is a deliberate five-fold relaxation, taken by the
 * builder in round 4 and owed to the owner as a decision rather than a patch:
 *
 *   - The three-minute figure could only be kept by a boat doing 61.5 knots across a 6.9
 *     km sea, and it made 97.37% of the chart's ETAs read exactly 3:00. A readout that
 *     always says the same thing is worse than none.
 *   - Arriving is not on the critical path of anything. Position feeds no rate: src/sim
 *     has no notion of where the boat is, progression settles from timestamps, and the
 *     player's one verb is choosing what to fish for. A passage costs the player the place
 *     they are looking at and nothing else.
 *   - The named anti-bars are about not being able to FIND things (Melvor: "constantly
 *     requires either the wiki or discord to progress") and about repeated manual actions
 *     (Bloobs: "gets insanely obnoxious after doing it multiple times"). One selection, an
 *     exact ETA and a bearing answer both; the worst-case duration answers neither.
 */
export const PASSAGE_CEILING_MINUTES = 20;

// ---------------------------------------------------------------------------
// Water regions. Continuous fields over the plane; see regions.ts.
// ---------------------------------------------------------------------------

/** DERIVED, leagues. Radius at which the openness field saturates at 1. */
export const OPENNESS_SCALE_LEAGUES = VOYAGE_GEOMETRY.opennessScaleLeagues;

/** DERIVED, leagues. Full north-south span of the chill field. */
export const CHILL_SCALE_LEAGUES = VOYAGE_GEOMETRY.chillScaleLeagues;

/** DERIVED, leagues. Lattice period of the field noise. 2.67 frame widths. */
export const FIELD_NOISE_PERIOD_LEAGUES = VOYAGE_GEOMETRY.fieldNoisePeriodLeagues;

/** DERIVED, leagues. Peak lateral wander the noise adds to a field isoline. */
export const FIELD_NOISE_AMPLITUDE_LEAGUES = VOYAGE_GEOMETRY.fieldNoiseAmplitudeLeagues;

/**
 * MODELLED, squared field units. Softness of the region blend. Sets the width of the
 * band where two waters mix: a little under one frame width, so a boundary crossing
 * reads as a gradient in water colour rather than a seam.
 */
export const REGION_BLEND_TEMPERATURE = 0.03;

/** MODELLED. Fixed seed for the field noise. Changing it re-shapes every boundary. */
export const FIELD_NOISE_SEED = 20260831;
